import { useEffect, useMemo, useState } from 'react';

import { getSelectorsByUserAgent } from 'react-device-detect';
import sanitizeHtml from 'sanitize-html';
import { isInContextualBanditsSubAnchor } from 'src/pages/software/confluence/anchors/subAnchors/subAnchor-util';

import { ProductKey } from '@/components/signup/utils/products';
import { hasJiraImcCampaignKey } from '@/components/signup/utils/url';
import { GrowthUnifiedProfile } from '@/libraries/growth-unified-profile-client/unified-profile.schema';
import { useGupsStatus } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups';
import {
	LANDING_GATE_TECH_TEAM_TYPES,
	UserSegmentationTeamTypes,
} from '@/templates/ondemand/signup/confirmation/confirmation-page/src/models/user-segmentation';
import { Cookie } from '@/utils/cookie';
import { decodeQueryParamValue } from '@/utils/decode-query-param-value';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag.ts';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest.ts';
import { delocalizePathname } from '@/utils/localization';
import { useLocation } from '@/utils/location-provider';
import { useEnglishLocale } from '@/utils/sentry-intl';
import { useSSRLoginState } from '@/utils/ssr-login-state-provider';
import { useIsMobile } from '@/utils/useIsMobile';

import { useUserContext } from '../user-context-provider';

import { daysAgo } from './days-ago';
import { useProductIntent } from './use-product-intent';

export const COHORTS = Object.freeze({
	NEW_USER: 'new-user',
	EXISTING_USER: 'existing-user',
	MANAGED_NEW_USER: 'managed-new-user',
	MANAGED_USER: 'managed-user',
	WEEK_ONE_USER: 'week-one-user',
	UPGRADE_ELIGIBLE_USER: 'upgrade-eligible-user',
	UPLIFT_ELIGIBLE_USER: 'uplift-eligible-user',
	CURRENT_PRODUCT_JOINABLE_USER: 'current-product-joinable-user',
	CURRENT_PRODUCT_USER: 'current-product-user',
	CROSSFLOW_ELIGIBLE_USER: 'crossflow-eligible-user',
});

export type Cohort = keyof typeof COHORTS;

export const JIRA_AI_NATIVE_SIGNUP_SOURCE = 'jira-ai-native';

export const DEVELOPER_BUNDLE = 'developer';

export const useEligibleForJsmLandUseCaseOnboarding = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	const urlParams = new URLSearchParams(window.location.search);
	const products = urlParams.get('products') ?? '';
	const primaryCollectionProduct = urlParams.get('primaryCollectionProduct');

	return (
		products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT) ||
		(products.includes(ProductKey.SERVICE_COLLECTION) &&
			primaryCollectionProduct === ProductKey.JIRA_SERVICE_MANAGEMENT)
	);
};

/**
 * Get user's primary cohort
 *
 * @param useContactExpandCohortLogic boolean - whether to use alternate cohort logic for contact and company expand experiments
 *
 * @returns Cohort | undefined
 */
// eslint-disable-next-line complexity
export const useCohort = (useContactExpandCohortLogic = false) => {
	const { profile, product } = useUserContext();
	const isManaged = useUserIsManaged();
	const isManagedUser = useUserIsManagedByGuardOrgAndHasSites();
	const isWeekOneUser = useProductD0to6Site({
		productKey: product?.key,
	});
	const isNewUser = useNewUser();
	const isUpgradeEligible = useIsUpgradeEligible();
	const isUpliftEligible = useIsUpliftEligible();
	const isCurrentProductJoinableUser = useIsCurrentProductJoinableUser();
	const isCurrentProductUser = useIsCurrentProductUser();
	const isCrossflowEligible = useIsCrossflowEligible();

	if (profile.requestInProgress) {
		return undefined;
	}

	if (useContactExpandCohortLogic) {
		if (isUpgradeEligible) {
			return COHORTS.UPGRADE_ELIGIBLE_USER;
		}
		if (isCurrentProductUser) {
			return COHORTS.CURRENT_PRODUCT_USER;
		}
		if (isCurrentProductJoinableUser) {
			return COHORTS.CURRENT_PRODUCT_JOINABLE_USER;
		}
		if (isUpliftEligible) {
			return COHORTS.UPLIFT_ELIGIBLE_USER;
		}
		if (isCrossflowEligible) {
			return COHORTS.CROSSFLOW_ELIGIBLE_USER;
		}
		if (isManaged) {
			return COHORTS.MANAGED_NEW_USER;
		}
		return COHORTS.NEW_USER;
	}

	if (isManagedUser) {
		return COHORTS.MANAGED_USER;
	}
	if (isUpgradeEligible) {
		return COHORTS.UPGRADE_ELIGIBLE_USER;
	}
	if (isWeekOneUser) {
		return COHORTS.WEEK_ONE_USER;
	}
	if (isCurrentProductUser) {
		return COHORTS.CURRENT_PRODUCT_USER;
	}
	if (isUpliftEligible) {
		return COHORTS.UPLIFT_ELIGIBLE_USER;
	}
	if (isNewUser) {
		return COHORTS.NEW_USER;
	}
	if (isCrossflowEligible) {
		return COHORTS.CROSSFLOW_ELIGIBLE_USER;
	}
	return COHORTS.EXISTING_USER;
};

/**
 * User is logged in and has only one site which was created in the last week and is Confluence-only
 * @returns boolean | undefined
 */
export const useConfluenceD0to6 = () => {
	const d1to6ConfluenceSite = useProductD0to6Site({
		productKey: ProductKey.CONFLUENCE,
	});
	return !d1to6ConfluenceSite ? d1to6ConfluenceSite : true;
};

/**
 * User's first name
 * @returns string | undefined
 */
export const useFirstName = () => {
	const { user } = useUserContext();
	if (user['requestInProgress']) {
		return undefined;
	}
	return (user['name'] ?? '').split(' ')[0];
};

/**
 * Returns true if the user has only one site which was created in the two weeks and is Jira-only
 * @returns boolean | undefined
 */
export const useJiraD0toW2 = () => {
	const d1toW2JiraSite = useJiraD0toW2Site();
	return !d1toW2JiraSite ? d1toW2JiraSite : true;
};

/**
 * Returns true if the user has only one site which was created within 6 days and is Jira-only
 * @returns boolean | undefined
 */
export const useJiraD0to6 = () => {
	const jiraD0to6Site = useProductD0to6Site({
		productKey: ProductKey.JIRA_SOFTWARE,
	});
	return !jiraD0to6Site ? jiraD0to6Site : true;
};

/**
 * User is logged in and has a single Jira site created in the last 2 weeks and is coming from Jira.com
 * @returns boolean | undefined
 */
export const useJiraD0toW2Site = () => {
	const {
		profile: { attributes, requestInProgress },
		referrer,
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) return undefined;
		if (attributes?.existingSites.length !== 1 || referrer !== 'jira.com') {
			return false;
		}

		const twoWeeksAgo = daysAgo(14);

		return (
			attributes?.existingSites.find(
				({ siteCreatedAt, products }) =>
					siteCreatedAt > twoWeeksAgo &&
					products.some(({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE) &&
					products.every(
						(product) =>
							product.productKey === ProductKey.JIRA_SOFTWARE ||
							product.productKey === ProductKey.ROVO,
					),
			) ?? false
		);
	}, [requestInProgress, referrer]);
};

/**
 * Returns true if the user has multiple Jira sites or one Jira site created more than 7 days ago
 * @returns boolean | undefined
 */
export const useJiraW2AndAfterOrMultipleJiras = () => {
	const oneW2OrOlderJiraSite = useOneW2OrOlderJiraSite();
	const multipleJiraSites = useMultipleJiraSites();

	if (oneW2OrOlderJiraSite === undefined || multipleJiraSites === undefined) {
		return undefined;
	}

	return oneW2OrOlderJiraSite || multipleJiraSites;
};

/**
 * User is logged in and has a single Jira site created more than 7 days ago
 * @returns boolean | undefined
 */
export const useOneW2OrOlderJiraSite = () => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) return undefined;

		const oneWeekAgo = daysAgo(7);

		const matchingSitesCount =
			attributes?.existingSites.filter(
				({ siteCreatedAt, products }) =>
					siteCreatedAt < oneWeekAgo &&
					products.some(({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE),
			).length ?? 0;

		return matchingSitesCount === 1;
	}, [requestInProgress]);
};

/**
 * User is logged in and has a multiple Jira sites
 * @returns boolean | undefined
 */
export const useMultipleJiraSites = () => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) return undefined;

		const matchingSitesCount =
			attributes?.existingSites.filter(({ products }) =>
				products.some(({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE),
			).length ?? 0;

		return matchingSitesCount > 1;
	}, [requestInProgress]);
};

/**
 * User is logged out
 * @returns boolean | undefined
 */
export const useLoggedOut = () => {
	const {
		user: { requestInProgress, isUserLoggedIn },
	} = useUserContext();
	const { hasCloudSessionTokenCookie } = useSSRLoginState();

	if (typeof window === 'undefined') {
		// If SSR return if cloud session token cookie is present
		return hasCloudSessionTokenCookie === false;
	}

	if (requestInProgress) {
		// Cloud session token cookie is created as http only cookie, so can't reference from browser's JS.
		// Instead, using login_user_detected cookie.
		const loginUserDetectedCookie = Cookie.readCookie('login_user_detected');
		return loginUserDetectedCookie === undefined || loginUserDetectedCookie !== 'true';
	}
	return isUserLoggedIn === false;
};

const INDIA_COUNTRY_ISO_CODE = 'IN';

type WindowWithCountryCode = Window & {
	countryCode?: string;
};

export const getWindowCountryCode = (): string | undefined => {
	if (typeof window === 'undefined') {
		return undefined;
	}

	return (window as WindowWithCountryCode).countryCode;
};

/**
 * User is logged out, their IP-resolved country is India (`IN`), and they are
 * visiting a Jira Software page.
 * Uses `window.countryCode` from the SSR `/location` bootstrap, not URL locale.
 */
export const useLoggedOutInIndiaForJira = () => {
	const loggedOut = useLoggedOut();
	const isJiraPath = useLocalizedPathnames(['/software/jira/*']);

	return Boolean(loggedOut && getWindowCountryCode() === INDIA_COUNTRY_ISO_CODE && isJiraPath);
};

/**
 * User is logged in
 * @returns boolean | undefined
 */
export const useLoggedIn = () => {
	const {
		user: { requestInProgress, isUserLoggedIn },
	} = useUserContext();

	if (requestInProgress) {
		return undefined;
	}

	return isUserLoggedIn;
};

/**
 * User is logged out and has Jira
 * @returns boolean | undefined
 */
export const useLoggedOutHasJira = () => {
	const loggedOut = useLoggedOut();
	const { loggedOutProfile } = useUserContext();

	if (loggedOut === undefined) {
		return undefined;
	}
	if (!loggedOut) {
		return false;
	}
	return loggedOutProfile?.sites?.some(({ products }) =>
		products?.some(({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE),
	);
};

/**
 * User is logged out and is in the Confluence flow (any edition)
 * @returns boolean | undefined
 */
export const useLoggedOutConfluence = () => {
	const confluence = useConfluenceIntent();
	const loggedOut = useLoggedOut();
	return confluence && loggedOut;
};

/**
 * User is logged in and is in the Bitbucket flow (any edition)
 * @returns boolean | undefined
 */
export const useLoggedInBitbucket = () => {
	const bitbucket = useBitbucketIntent();
	const loggedIn = useLoggedIn();
	return bitbucket && loggedIn;
};

/**
 * User is logged out and is in the Jira flow (any edition)
 * @returns boolean | undefined
 */
export const useLoggedOutJira = () => {
	const jira = useJiraIntent();
	const loggedOut = useLoggedOut();
	return jira && loggedOut;
};

/**
 * User is logged out and does not have Jira
 * @returns boolean | undefined
 */
export const useLoggedOutNoJira = () => {
	const loggedOut = useLoggedOut();
	const { loggedOutProfile } = useUserContext();

	if (loggedOut === undefined) {
		return undefined;
	}
	if (!loggedOut) {
		return false;
	}

	return (
		!loggedOutProfile ||
		loggedOutProfile.sites?.every(
			({ products }) =>
				!products?.some(({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE),
		)
	);
};

/**
 * User is logged out and is in the Jira free flow
 * @returns boolean | undefined
 */
export const useLoggedOutJiraFree = () => {
	const jira = useJiraIntent();
	const free = useFreeIntent();
	const loggedOut = useLoggedOut();
	return jira && free && loggedOut;
};

/**
 * User is logged out and is in the Jira free flow, expressed via the
 * `useJiraFreeIntent` helper. Used to target the recommend-work-email signup
 * experiment, which only runs for logged-out visitors on the Jira free signup.
 * @returns boolean | undefined
 */
export const useLoggedOutJiraFreeIntent = () => {
	const jiraFree = useJiraFreeIntent();
	const loggedOut = useLoggedOut();
	return jiraFree && loggedOut;
};

/**
 * User is NOT arriving from the Jira IMC paid campaign
 * (identified via the `utm_campaign` URL param).
 * @returns boolean
 */
export const useExcludeJiraImcCampaign = () => {
	const { search } = useLocation();
	const utmCampaign = new URLSearchParams(search).get('utm_campaign');
	return !hasJiraImcCampaignKey(utmCampaign);
};

/**
 * User is logged in and has no sites
 * @returns boolean | undefined
 */
export const useNewUser = () => {
	const {
		profile: { requestInProgress, cohorts },
	} = useUserContext();
	if (requestInProgress) return undefined;
	return cohorts?.userType === 'new-user';
};

/**
 * Contact-expand "new-user" cohort used by LoggedInJiraFree (useCohort(true)).
 *
 * Not the same as useNewUser(): that is raw cohorts.userType === 'new-user', while
 * useCohort(true) applies contact-expand precedence (upgrade / current-product /
 * joinable / uplift / crossflow / managed-new-user first). Match this hook to the
 * free signup new-user branch so managed / week-one / existing users are not
 * enrolled when ExperimentView is evaluated.
 *
 * @returns boolean | undefined
 */
export const useLoggedInNewUser = () => {
	const cohort = useCohort(true);
	if (cohort === undefined) return undefined;
	return cohort === COHORTS.NEW_USER;
};

/**
 * Get first site with given productKey created in the last 7 days
 * @param productKey string
 * @returns object | undefined
 */
export const useProductD0to6Site = ({ productKey }: { productKey?: ProductKey }) => {
	const {
		profile: { requestInProgress, attributes },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) return undefined;
		if (attributes?.existingSites.length !== 1) {
			return false;
		}

		const oneWeekAgo = daysAgo(7);

		// sites may only have the current productKey, rovo, project, or goal
		const weekOneProductKeys = [productKey, ProductKey.ROVO, 'project', 'goal'];

		return (
			attributes?.existingSites.find(({ siteCreatedAt, products }) => {
				const isRecent = siteCreatedAt > oneWeekAgo;
				const hasTargetProduct = products.some((product) => product.productKey === productKey);
				const allProductsAllowed = products.every(({ productKey }) =>
					weekOneProductKeys.includes(productKey),
				);
				return isRecent && hasTargetProduct && allProductsAllowed;
			}) ?? false
		);
	}, [requestInProgress, productKey]);
};

/**
 * Get all sites with the current product
 *
 * @returns array | undefined
 */
export const useSitesWithCurrentProduct = () => {
	const {
		product,
		profile: { attributes, requestInProgress },
	} = useUserContext();

	return useMemo(() => {
		if (requestInProgress) {
			return undefined;
		}

		if (!product?.key) {
			return [];
		}

		return (
			attributes?.existingSites.filter((site) =>
				site.products.some(
					({ productKey, partOfCollection }) =>
						productKey === product?.key || partOfCollection === product?.key,
				),
			) ?? []
		);
	}, [requestInProgress, attributes?.existingSites, product?.key]);
};

/**
 * User is logged in and has one or more Jira sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJira = () => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) {
			return undefined;
		}

		return (
			attributes?.existingSites.some((sites) =>
				sites.products.some((product) => product.productKey === ProductKey.JIRA_SOFTWARE),
			) ?? false
		);
	}, [requestInProgress]);
};

/**
 * User is logged in and has a Jira site created between D1 and D14.
 * When cloudSiteId is provided, only that site is considered.
 * This uses site age because profile creation date is not available in the WAC profile context.
 * @returns boolean | undefined
 */
export const useLoggedInHasJiraSiteD1toD14 = (cloudSiteId?: string) => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	const existingSites = attributes?.existingSites;

	return useMemo(() => {
		if (requestInProgress) {
			return undefined;
		}

		const oneDayAgo = daysAgo(1);
		const fourteenDaysAgo = daysAgo(14);

		return (
			existingSites?.some(({ cloudId, siteCreatedAt, products }) => {
				if (cloudSiteId && cloudId !== cloudSiteId) {
					return false;
				}

				const siteIsD1ToD14 = siteCreatedAt >= fourteenDaysAgo && siteCreatedAt <= oneDayAgo;
				const siteHasJiraSoftware = products.some(
					({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE,
				);

				return siteIsD1ToD14 && siteHasJiraSoftware;
			}) ?? false
		);
	}, [cloudSiteId, existingSites, requestInProgress]);
};

/**
 * User is logged in and has one or more Confluence sites
 * @returns boolean | undefined
 */
export const useLoggedInHasConfluence = () => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) {
			return undefined;
		}

		return (
			attributes?.existingSites.some((sites) =>
				sites.products.find((product) => product.productKey === ProductKey.CONFLUENCE),
			) ?? false
		);
	}, [requestInProgress]);
};

/**
 * User is logged in and has one or more JSM sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJSM = () => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) {
			return undefined;
		}

		return (
			attributes?.existingSites.some((sites) =>
				sites.products.find((product) => product.productKey === ProductKey.JIRA_SERVICE_MANAGEMENT),
			) ?? false
		);
	}, [requestInProgress]);
};

/**
 * User is logged in and has one or more Loom sites
 * @returns boolean | undefined
 */
export const useLoggedInHasLoom = () => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) {
			return undefined;
		}

		return (
			attributes?.existingSites.some((sites) =>
				sites.products.find((product) => product.productKey === ProductKey.LOOM),
			) ?? false
		);
	}, [requestInProgress]);
};

/**
 * User is logged in and has one or more JPD sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJPD = () => {
	const {
		profile: { attributes, requestInProgress },
	} = useUserContext();
	return useMemo(() => {
		if (requestInProgress) {
			return undefined;
		}

		return (
			attributes?.existingSites.some((sites) =>
				sites.products.find((product) => product.productKey === ProductKey.JIRA_PRODUCT_DISCOVERY),
			) ?? false
		);
	}, [requestInProgress]);
};

/**
 * User is logged in AND has one or more Jira sites AND has one or more Confluence sites
 * @returns boolean | undefined
 */
export const useLoggedInHasJiraAndConfluence = () => {
	const hasJiraSites = useLoggedInHasJira();
	const hasConfluenceSites = useLoggedInHasConfluence();

	return hasJiraSites && hasConfluenceSites;
};

const JSM_ELIGIBLE_JOB_FUNCTIONS = new Set([
	'engineering',
	'information technology',
	'customer service',
	'operations',
	'human resources',
	'data science',
]);

const LOOM_ELIGIBLE_JOB_FUNCTIONS = new Set([
	'program management',
	'finance',
	'legal',
	'c-suite',
	'other',
	'undefined',
	null,
	'sales',
	'marketing',
	'design',
]);

const JPD_ELIGIBLE_JOB_FUNCTIONS = new Set(['product management']);

/**
 * User has a job function that pairs with JSM
 * @returns boolean | undefined
 */
export const useJSMJobFunction = () => {
	const { getUserProfileJobFunctionCallback } = useGupsStatus();
	const [hasJSMJobFunction, setHasJSMJobFunction] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		const checkTeamType = async () => {
			const result = await getUserProfileJobFunctionCallback('homepage-xflow-job-function');
			const jobFunction = (result as { data?: GrowthUnifiedProfile['userProfile'] })?.data
				?.jobFunction;
			const matches = jobFunction && JSM_ELIGIBLE_JOB_FUNCTIONS.has(jobFunction);
			setHasJSMJobFunction(matches || false);
		};

		checkTeamType();
	}, [getUserProfileJobFunctionCallback]);

	return hasJSMJobFunction;
};

/**
 * User is in the JSM experiment cohort
 * @returns boolean
 */
export const useLoggedInHasJSMCohortQualification = () => {
	const hasJSMJobFunction = useJSMJobFunction();
	const hasJiraAndConfluence = useLoggedInHasJiraAndConfluence();
	const isEnglishLocale = useEnglishLocale();

	return hasJSMJobFunction && hasJiraAndConfluence && isEnglishLocale;
};

/**
 * User has a job function that pairs with JPD
 * @returns boolean | undefined
 */
export const useJPDJobFunction = () => {
	const { getUserProfileJobFunctionCallback } = useGupsStatus();
	const [hasJPDJobFunction, setHasJPDJobFunction] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		const checkTeamType = async () => {
			const result = await getUserProfileJobFunctionCallback('homepage-xflow-job-function');
			const jobFunction = (result as { data?: GrowthUnifiedProfile['userProfile'] })?.data
				?.jobFunction;
			const matches = jobFunction && JPD_ELIGIBLE_JOB_FUNCTIONS.has(jobFunction);
			setHasJPDJobFunction(matches || false);
		};

		checkTeamType();
	}, [getUserProfileJobFunctionCallback]);

	return hasJPDJobFunction;
};

/**
 * User is in the JDP experiment cohort
 * @returns boolean
 */
export const useLoggedInHasJPDCohortQualification = () => {
	const hasJPDJobFunction = useJPDJobFunction();
	const hasJiraAndConfluence = useLoggedInHasJiraAndConfluence();
	const isEnglishLocale = useEnglishLocale();

	return hasJPDJobFunction && hasJiraAndConfluence && isEnglishLocale;
};

/**
 * User has a team type that pairs with Loom
 * @returns boolean | undefined
 */
export const useLoomJobFunction = () => {
	const { getUserProfileJobFunctionCallback } = useGupsStatus();
	const [hasLoomJobFunction, setHasLoomJobFunction] = useState<boolean | undefined>(undefined);

	useEffect(() => {
		const checkTeamType = async () => {
			const result = await getUserProfileJobFunctionCallback('homepage-xflow-job-function');
			const jobFunction = (result as { data?: GrowthUnifiedProfile['userProfile'] })?.data
				?.jobFunction;
			const matches =
				jobFunction === null ||
				jobFunction === undefined ||
				LOOM_ELIGIBLE_JOB_FUNCTIONS.has(jobFunction);
			setHasLoomJobFunction(matches || false);
		};

		checkTeamType();
	}, [getUserProfileJobFunctionCallback]);

	return hasLoomJobFunction;
};

/**
 * User is in the Loom experiment cohort
 * @returns boolean
 */
export const useLoggedInHasLoomCohortQualification = () => {
	const hasLoomJobFunction = useLoomJobFunction();
	const hasJiraAndConfluence = useLoggedInHasJiraAndConfluence();
	const isEnglishLocale = useEnglishLocale();

	return hasLoomJobFunction && hasJiraAndConfluence && isEnglishLocale;
};

/**
 * User is in the JSM Xflow experiment cohort
 * @returns boolean
 */
export const useHomePageJSMXflowTargetingCohort = () => {
	const hasJSMCohortQualification = useLoggedInHasJSMCohortQualification();
	const hasLoomCohortQualification = useLoggedInHasLoomCohortQualification();
	const hasJPDCohortQualification = useLoggedInHasJPDCohortQualification();
	const hasJSM = useLoggedInHasJSM();
	const hasLoom = useLoggedInHasLoom();
	const hasJPD = useLoggedInHasJPD();

	if (
		// “JSM team types | job function” and no JSM entitlement
		hasJSMCohortQualification &&
		!hasJSM
	) {
		return true;
	} else if (
		// “Loom team types | job function” and has loom and no JSM entitlement
		hasLoomCohortQualification &&
		hasLoom &&
		!hasJSM
	) {
		return true;
	} else if (
		// “JPD team types | job function” and has loom and JPD entitlement and no JSM entitlement
		hasJPDCohortQualification &&
		hasLoom &&
		hasJPD &&
		!hasJSM
	) {
		return true;
	}
	return false;
};

/**
 * User is in the JPD Xflow experiment cohort
 * @returns boolean
 */
export const useHomePageJPDXflowTargetingCohort = () => {
	const hasJSMCohortQualification = useLoggedInHasJSMCohortQualification();
	const hasLoomCohortQualification = useLoggedInHasLoomCohortQualification();
	const hasJPDCohortQualification = useLoggedInHasJPDCohortQualification();
	const hasJSM = useLoggedInHasJSM();
	const hasLoom = useLoggedInHasLoom();
	const hasJPD = useLoggedInHasJPD();

	if (
		// “JPD team types | job function” and no JPD entitlement
		hasJPDCohortQualification &&
		!hasJPD
	) {
		return true;
	} else if (
		// “Loom team types | job function” and has loom and has JSM and no JPD entitlement
		hasLoomCohortQualification &&
		hasLoom &&
		hasJSM &&
		!hasJPD
	) {
		return true;
	} else if (
		// “JSM team types | job function” and has JSM and has loom and no JPD entitlement
		hasJSMCohortQualification &&
		hasJSM &&
		hasLoom &&
		!hasJPD
	) {
		return true;
	}
	return false;
};

/**
 * User is in the Loom Xflow experiment cohort
 * @returns boolean
 */
export const useHomePageLoomXflowTargetingCohort = () => {
	const hasJSMCohortQualification = useLoggedInHasJSMCohortQualification();
	const hasLoomCohortQualification = useLoggedInHasLoomCohortQualification();
	const hasJPDCohortQualification = useLoggedInHasJPDCohortQualification();
	const hasJSM = useLoggedInHasJSM();
	const hasLoom = useLoggedInHasLoom();
	const hasJPD = useLoggedInHasJPD();

	if (
		// "Loom team types | job function” and no Loom entitlement
		hasLoomCohortQualification &&
		!hasLoom
	) {
		return true;
	} else if (
		// “JSM team types | job function” and has JSM and no Loom entitlement
		hasJSMCohortQualification &&
		hasJSM &&
		!hasLoom
	) {
		return true;
	} else if (
		// “JPD team types | job function” and has JPD and no Loom entitlement
		hasJPDCohortQualification &&
		hasJPD &&
		!hasLoom
	) {
		return true;
	}
	return false;
};

/**
 * Returns true if the user prefers dark color scheme
 * @returns boolean
 */
export const usePrefersDarkColorScheme = () => {
	return usePrefersColorScheme('dark');
};

/**
 * Returns true if the user prefers the specified color scheme and is on a specific path
 * @param color "dark" | "light"
 * Returns true if the user prefers the specified color scheme and is on a specific path
 * This is used to determine if dark mode should be applied based on user preference
 * and the current path.
 * @returns boolean
 */
export const usePrefersColorScheme = (color: 'dark' | 'light') => {
	return window?.matchMedia && window?.matchMedia(`(prefers-color-scheme: ${color})`).matches
		? true
		: false;
};

/**
 * User is in the Confluence free flow
 * @returns boolean
 */
export const useConfluenceFreeIntent = () => {
	const isConfluence = useConfluenceIntent();
	const isFree = useFreeIntent();
	return isConfluence && isFree;
};

/**
 * User is in the Compass flow (any edition)
 * @returns boolean
 */
export const useCompassIntent = () => {
	const { product } = useUserContext();
	return product?.key === ProductKey.COMPASS;
};

/**
 * User is in the Bitbucket flow (any edition)
 * @returns boolean
 */
export const useBitbucketIntent = () => {
	const { product } = useUserContext();
	return product?.name === 'bitbucket';
};

/**
 * User is in the Confluence flow (any edition)
 * @returns boolean
 */
export const useConfluenceIntent = () => {
	const { product } = useUserContext();
	return product?.name === 'confluence';
};

/**
 * User is in a free flow (any product)
 * @returns boolean
 */
export const useFreeIntent = () => {
	const { edition } = useUserContext();
	return edition === 'free';
};

/**
 * User is in a standard flow (any product)
 * @returns boolean
 */
export const useStandardIntent = () => {
	const { edition } = useUserContext();
	return edition === 'standard';
};

/**
 * User is in a premium flow (any product)
 * @returns boolean
 */
export const usePremiumIntent = () => {
	const { edition } = useUserContext();
	return edition === 'premium';
};

/**
 * User is in the Jira flow (any edition)
 * @returns boolean
 */
export const useJiraIntent = () => {
	const { product } = useUserContext();
	return product?.name === 'jira';
};

/**
 * User is in the Jira flow (free edition)
 * @returns boolean
 */
export const useJiraFreeIntent = () => {
	const { edition, product } = useUserContext();
	return product?.name === 'jira' && edition === 'free';
};

/**
 * User is in the Jira Service Management free flow
 * @returns boolean
 */
export const useJiraServiceManagementFreeIntent = () => {
	const isJiraServiceManagement = useJiraServiceManagementIntent();
	const isFree = useFreeIntent();
	return isJiraServiceManagement && isFree;
};

/**
 * User is in the Jira Service Management flow (any edition)
 * @returns boolean
 */
export const useJiraServiceManagementIntent = () => {
	const { product } = useUserContext();
	return product?.name === 'jira-servicedesk';
};

/**
 * User is in the Jira Product Discovery free flow
 * @returns boolean
 */
export const useJiraProductDiscoveryFreeIntent = () => {
	const isJiraProductDiscovery = useJiraProductDiscoveryIntent();
	const isFree = useFreeIntent();
	return isJiraProductDiscovery && isFree;
};

/**
 * User is in the Jira Product Discovery flow (any edition)
 * @returns boolean
 */
export const useJiraProductDiscoveryIntent = () => {
	const { product } = useUserContext();
	return product?.name === 'jira-product-discovery';
};

/**
 * User is in the Dev AI flow
 * @returns boolean
 */
export const useDevAiIntent = () => {
	const { product } = useUserContext();
	return product?.name === 'devai';
};

/**
 * User is in the Loom flow (any edition)
 * @returns boolean
 */
export const useLoomIntent = () => {
	const { product } = useUserContext();
	return product?.name === 'loom';
};

export const useManagingOrgHasGuard = () => {
	const { profile } = useUserContext();

	// todo share with bxp-ui-signup's orgBasedOfferingIdMap
	const GUARD_PREMIUM = '2f5e5808-d501-450a-b246-b0bd50a0b72a';
	const GUARD_STANDARD = '1ac3b7dd-7141-414e-bc00-c890acc458b0';
	const GUARD_HAMS = 'GUARD_HAMS';
	const GUARD_OFFERING_IDS = [GUARD_PREMIUM, GUARD_STANDARD, GUARD_HAMS];

	if (profile.requestInProgress) {
		return undefined;
	}

	return profile.attributes?.managingOrg?.offeringIds?.some((offeringId) =>
		GUARD_OFFERING_IDS.includes(offeringId),
	);
};

export const useManagingOrgUserSites = () => {
	const { product, profile } = useUserContext();

	if (profile.requestInProgress) {
		return undefined;
	}

	return profile.attributes?.existingSites.filter(
		({ cloudId, products }) =>
			profile.attributes?.managingOrg?.cloudIds?.includes(cloudId) &&
			products.some(({ productKey }) => productKey === product?.key),
	);
};

// Product Collection: sites with its constituents (JPD / Feedback) are switched via uplift, not crossflow
const isProductCollectionConstituentSite = (
	productKey: string,
	products: { productKey: string }[],
): boolean =>
	productKey === ProductKey.PRODUCT_COLLECTION &&
	products.some(({ productKey: siteProductKey }) =>
		[ProductKey.JIRA_PRODUCT_DISCOVERY, ProductKey.FEEDBACK].includes(siteProductKey as ProductKey),
	);

/**
 * Returns user's existing sites that do not have the current product and the user has admin access to
 *
 * @return {ExistingSite[]} array of existing sites
 */
export const useCrossflowEligibleSites = () => {
	const { product, profile } = useUserContext();
	return useMemo(
		() =>
			profile.attributes?.existingSites.filter(
				({ hasAdminAccess, products }) =>
					product &&
					hasAdminAccess &&
					!isProductCollectionConstituentSite(product.key, products) &&
					!products.some(({ productKey }) =>
						product.key === ProductKey.SERVICE_COLLECTION
							? // exclude sites with servco constituent products from site activation crossflow
								[ProductKey.JIRA_SERVICE_MANAGEMENT, ProductKey.JIRA_CUSTOMER_SERVICE].includes(
									productKey as ProductKey,
								)
							: productKey === product.key,
					),
			) ?? [],
		[profile.attributes?.existingSites, product],
	);
};

export const useIsCrossflowEligible = () => {
	const crossflowEligibleSites = useCrossflowEligibleSites();
	return crossflowEligibleSites?.length > 0;
};

export const useUserIsManaged = () => {
	const { profile } = useUserContext();

	if (profile.requestInProgress) {
		return undefined;
	}

	return !!profile.attributes?.hasAccountManagedByOrg;
};

export const useUserIsManagedByGuardOrgAndHasSites = () => {
	const { profile } = useUserContext();
	const managingOrgHasGuard = useManagingOrgHasGuard();
	const managingOrgUserSites = useManagingOrgUserSites();

	if (profile.requestInProgress) {
		return undefined;
	}

	return managingOrgHasGuard && managingOrgUserSites?.length;
};

export const useLoggedInHasJiraAndNotConfluence = () => {
	const {
		profile: { requestInProgress: profileRequestInProgress, attributes },
		user: { requestInProgress: userRequestInProgress },
	} = useUserContext();
	return useMemo(() => {
		// Checking for both user and profile request in progress,
		// since profileRequestInProgress is initially defined as 'false' and then gets updated to true when the request is made.
		// This is probably not needed since this experiment is wrapped within <LoggedInHeroContainer /> and has the same logic.
		// But adding it here incase this experiment is used outside of <LoggedInHeroContainer />
		if (userRequestInProgress || profileRequestInProgress) {
			return undefined;
		}
		return (
			(attributes?.existingSites.some((sites) =>
				sites.products.find((product) => product.productKey === ProductKey.JIRA_SOFTWARE),
			) &&
				!attributes?.existingSites.some((sites) =>
					sites.products.find((product) => product.productKey === ProductKey.CONFLUENCE),
				)) ??
			false
		);
	}, [userRequestInProgress, profileRequestInProgress]);
};

/**
 * Return true if given pathname matches current pathname
 * @param pathnames string[]
 * @returns boolean
 */
export const useSpecifiedPathnames = (pathnames: string[]) => {
	const { pathname: currentPathname } = useLocation();

	if (pathnames.includes(currentPathname)) {
		return true;
	}
	return false;
};

/**
 * Return true if given pathname matches current pathname
 * Supports wildcard patterns ending with /* to match path prefixes
 * @param pathnames string[] - Array of paths or patterns (e.g., "/software/confluence/*")
 * @returns boolean
 */
export const useLocalizedPathnames = (pathnames: string[]) => {
	const { pathname: currentPathname } = useLocation();
	const delocalizedPathname = delocalizePathname(currentPathname);

	return pathnames.some((pattern) => {
		if (pattern.endsWith('/*')) {
			// Wildcard: match prefix
			const prefix = pattern.slice(0, -2); // Remove the /*
			return delocalizedPathname === prefix || delocalizedPathname.startsWith(`${prefix}/`);
		}
		return delocalizedPathname === pattern;
	});
};

export const useIsSoftwareConfluencePath = () => {
	const loggedIn = useLoggedIn();
	const loggedOut = useLoggedOut();
	const isMobile = useIsMobile();
	const { profile, loggedOutProfile } = useUserContext();
	const isConfluencePath = useLocalizedPathnames(['/software/confluence/*']);

	if (loggedIn === undefined || loggedOut === undefined) {
		return undefined;
	}

	if (typeof window !== 'undefined' && window.cmsPageVariant) {
		return false;
	}

	const sites = loggedIn ? profile.attributes?.existingSites : loggedOutProfile?.sites;

	return (
		isConfluencePath &&
		isMobile &&
		(sites?.some((site) =>
			site.products?.some(({ productKey }) => productKey === ProductKey.CONFLUENCE),
		) ??
			false)
	);
};

export const useIsSoftwareJiraPath = () => {
	const loggedIn = useLoggedIn();
	const loggedOut = useLoggedOut();
	const isMobile = useIsMobile();
	const { profile, loggedOutProfile } = useUserContext();
	const isJiraPath = useLocalizedPathnames(['/software/jira/*']);
	const isJiraProductDiscoveryPath = useLocalizedPathnames(['/software/jira/product-discovery/*']);
	const isJiraAgilePath = useLocalizedPathnames(['/software/jira/agile/*']);

	if (loggedIn === undefined || loggedOut === undefined) {
		return undefined;
	}

	if (typeof window !== 'undefined' && window.cmsPageVariant) {
		return false;
	}

	const sites = loggedIn ? profile.attributes?.existingSites : loggedOutProfile?.sites;

	return (
		isJiraPath &&
		isMobile &&
		!isJiraProductDiscoveryPath &&
		!isJiraAgilePath &&
		(sites?.some((site) =>
			site.products?.some(({ productKey }) => productKey === ProductKey.JIRA_SOFTWARE),
		) ??
			false)
	);
};

/**
 * User is in a paid edition flow and has the free version of current product
 * @returns boolean
 */
export const useIsUpgradeEligible = () => {
	const { product, edition } = useUserContext();
	const upgradableSites = useUpgradableSites();

	// you need to be in a paid product edition flow
	if (!product || edition === 'free') {
		return undefined;
	}

	return !!upgradableSites?.length;
};

export const useIsUpliftEligible = () => {
	const upliftableSites = useUpliftableSites();
	const productCollectionUpliftableSites = useProductCollectionUpliftableSites();
	return !!upliftableSites?.length || !!productCollectionUpliftableSites?.length;
};

/**
 * User is eligible for standard to premium upgrade
 * @returns boolean | undefined
 */
export const useIsStandardToPremiumUpgradeEligible = () => {
	const upgradableStandardSites = useUpgradableStandardSites();
	return !!upgradableStandardSites?.length;
};

/**
 * User is eligible for the Contact / Company expand in Confluence experiment
 * @returns boolean
 */
export const useIsConfluenceContactCompanyExpandEligible = () => {
	const isConfluenceIntent = useConfluenceIntent();
	const isLoggedIn = useLoggedIn();
	const isPremiumIntent = usePremiumIntent();
	const s2pUpgradeEligible = useIsStandardToPremiumUpgradeEligible();
	return isConfluenceIntent && isLoggedIn && !(isPremiumIntent && s2pUpgradeEligible);
};

/**
 * User is eligible for the Contact / Company expand in Jira experiment
 * @returns boolean
 */
export const useIsJiraContactCompanyExpandEligible = () => {
	const isJiraIntent = useJiraIntent();
	const isLoggedIn = useLoggedIn();
	const isPremiumIntent = usePremiumIntent();
	const s2pUpgradeEligible = useIsStandardToPremiumUpgradeEligible();
	return isJiraIntent && isLoggedIn && !(isPremiumIntent && s2pUpgradeEligible);
};

/**
 * Get a list of sites that are eligible for premium upgrade (have standard edition of the current product)
 *
 * @returns {ExistingSite[] | undefined}
 */
export const useUpgradableStandardSites = () => {
	const {
		product,
		edition,
		profile: { attributes, requestInProgress },
	} = useUserContext();

	return useMemo(() => {
		if (!product?.key || requestInProgress) {
			return undefined;
		}

		const eligibleProducts = [
			ProductKey.JIRA_SOFTWARE,
			ProductKey.CONFLUENCE,
			ProductKey.JIRA_PRODUCT_DISCOVERY,
		];

		if (edition !== 'premium' || !eligibleProducts.includes(product.key)) {
			return undefined;
		}

		return (
			attributes?.existingSites.filter(
				({ hasAdminAccess, products }) =>
					hasAdminAccess &&
					products.some(
						({ productKey, productEdition, entitlementId, billingSourceSystem }) =>
							productKey === product.key &&
							productEdition === 'standard' &&
							entitlementId !== undefined &&
							billingSourceSystem !== undefined,
					),
			) ?? []
		);
	}, [product?.key, edition, attributes?.existingSites, requestInProgress]);
};

/**
 * Get a list of sites that are eligible for upgrade (have free edition of the current product)
 *
 * @returns {ExistingSite[] | undefined}
 */
export const useUpgradableSites = () => {
	const {
		product,
		profile: { attributes, requestInProgress },
	} = useUserContext();

	const isGateEnabledForEntitlementCheck = checkFeatureFlagGate(
		featureFlags.includeEntitlementIdCheckInCohortsGate,
	);

	return useMemo(() => {
		if (!product?.key || requestInProgress) {
			return undefined;
		}

		return (
			attributes?.existingSites.filter(({ products }) =>
				products.some(
					({ productKey, productEdition, partOfCollection, entitlementId, billingSourceSystem }) =>
						isGateEnabledForEntitlementCheck
							? (productKey === product.key || partOfCollection === product.key) &&
								productEdition === 'free' &&
								entitlementId !== undefined &&
								billingSourceSystem !== undefined
							: (productKey === product.key || partOfCollection === product.key) &&
								productEdition === 'free',
				),
			) ?? []
		);
	}, [
		product?.key,
		attributes?.existingSites,
		requestInProgress,
		isGateEnabledForEntitlementCheck,
	]);
};

/**
 * Get a list of sites that are not part of a collection and eligible for uplift (have constituent products of current collection)
 *
 * @returns {ExistingSite[] | undefined}
 */
export const useUpliftableSites = () => {
	const {
		product,
		profile: { attributes, requestInProgress },
	} = useUserContext();

	return useMemo(() => {
		if (!product?.key || requestInProgress) {
			return undefined;
		}

		// currently only applies to teamwork collection
		if (product.key !== ProductKey.TEAMWORK_COLLECTION) {
			return [];
		}

		const constituentProducts = [
			ProductKey.JIRA_SOFTWARE,
			ProductKey.CONFLUENCE,
			ProductKey.LOOM,
		] as string[];

		return (
			attributes?.existingSites.filter(({ products }) =>
				products.some(
					({ productKey, partOfCollection }) =>
						constituentProducts.includes(productKey) && !partOfCollection,
				),
			) ?? []
		);
	}, [product?.key, attributes?.existingSites, requestInProgress]);
};

const productCollectionUpliftEditions: string[] = ['free', 'standard', 'premium'];

/**
 * Get a list of sites with a standalone JPD on the same edition as the Product Collection intent,
 * which can be switched to Product Collection (STARB-4846). Enterprise is sales-led, so never offered.
 *
 * @returns {ExistingSite[] | undefined}
 */
export const useProductCollectionUpliftableSites = () => {
	const {
		product,
		edition,
		profile: { attributes, requestInProgress },
	} = useUserContext();

	return useMemo(() => {
		if (!product?.key || requestInProgress) {
			return undefined;
		}
		if (
			product.key !== ProductKey.PRODUCT_COLLECTION ||
			!edition ||
			!productCollectionUpliftEditions.includes(edition)
		) {
			return [];
		}
		return (
			attributes?.existingSites.filter(({ products }) =>
				products.some(
					({ productKey, productEdition, partOfCollection }) =>
						productKey === ProductKey.JIRA_PRODUCT_DISCOVERY &&
						productEdition === edition &&
						!partOfCollection,
				),
			) ?? []
		);
	}, [product?.key, edition, attributes?.existingSites, requestInProgress]);
};

export const useDeveloperBundleIntent = () => {
	const location = useLocation();
	const { bundle } = useProductIntent(location.href);
	return bundle === DEVELOPER_BUNDLE;
};

/**
 * User is in the Service Collection flow (any edition)
 * @returns {boolean|undefined}
 */
export const useServiceCollectionIntent = () => {
	const { product } = useUserContext();
	if (!product) {
		return undefined;
	}
	return product.key === ProductKey.SERVICE_COLLECTION;
};

/**
 * User is in the Statuspage flow (any edition)
 * @returns {boolean|undefined}
 */
export const useStatuspageIntent = () => {
	const { product } = useUserContext();
	if (!product) {
		return undefined;
	}
	return product.key === ProductKey.STATUSPAGE;
};

/**
 * User is in the Teamwork Collection flow (any edition)
 * @returns {boolean|undefined}
 */
export const useTeamworkCollectionIntent = () => {
	const { product } = useUserContext();
	if (!product) {
		return undefined;
	}
	return product.key === ProductKey.TEAMWORK_COLLECTION;
};

/**
 * User has the Service Collection (any edition)
 * @returns {boolean|undefined}
 */
export const useHasServiceCollection = () => {
	const { profile } = useUserContext();
	if (profile.requestInProgress) {
		return undefined;
	}
	return profile.attributes?.existingSites.some(({ products }) =>
		products.some(({ partOfCollection }) => partOfCollection === ProductKey.SERVICE_COLLECTION),
	);
};

/**
 * User has the Teamwork Collection (any edition)
 * @returns {boolean|undefined}
 */
export const useHasTeamworkCollection = () => {
	const { profile } = useUserContext();
	if (profile.requestInProgress) {
		return undefined;
	}
	return profile.attributes?.existingSites.some(({ products }) =>
		products.some(({ partOfCollection }) => partOfCollection === ProductKey.TEAMWORK_COLLECTION),
	);
};

/**
 * User has the Product Collection (any edition)
 * @returns {boolean|undefined}
 */
export const useHasProductCollection = () => {
	const { profile } = useUserContext();
	if (profile.requestInProgress) {
		return undefined;
	}
	return profile.attributes?.existingSites.some(({ products }) =>
		products.some(({ partOfCollection }) => partOfCollection === ProductKey.PRODUCT_COLLECTION),
	);
};

/**
 * User is in Desktop view
 * Uses request.headers['user-agent'] on server side
 * Uses window.navigator.userAgent on client side
 * @returns {boolean|undefined}
 */
export const useIsDesktop = () => {
	const { userAgent } = useSSRLoginState();
	const { isDesktop } = userAgent === undefined ? {} : (getSelectorsByUserAgent(userAgent) ?? {});
	return isDesktop;
};

/**
 * User can join a site with the current product
 * TODO: consider adding support for collections
 * TODO: consider making edition-aware
 * @returns {boolean|undefined}
 */
export const useIsCurrentProductJoinableUser = (): boolean | undefined => {
	const { product, profile } = useUserContext();
	if (profile.requestInProgress) {
		return undefined;
	}
	return profile.attributes?.existingProductsFromOrg.some(
		({ productKey }) => productKey === product?.key,
	);
};

/**
 * User has the current product
 * @returns {boolean|undefined}
 */
export const useIsCurrentProductUser = () => {
	const { product, profile } = useUserContext();
	const hasServiceCollection = useHasServiceCollection();
	const hasTeamworkCollection = useHasTeamworkCollection();
	const hasProductCollection = useHasProductCollection();
	if (profile.requestInProgress) {
		return undefined;
	}
	if (product?.key === ProductKey.SERVICE_COLLECTION) {
		return hasServiceCollection;
	}
	if (product?.key === ProductKey.TEAMWORK_COLLECTION) {
		return hasTeamworkCollection;
	}
	if (product?.key === ProductKey.PRODUCT_COLLECTION) {
		return hasProductCollection;
	}
	return profile.attributes?.existingSites.some(({ products }) =>
		products.some(({ productKey }) => productKey === product?.key),
	);
};

/**
 * User is logged out and has existing sites
 * @returns boolean | undefined
 */
export const useLoggedOutHasExistingSites = () => {
	const loggedOut = useLoggedOut();
	const { loggedOutProfile } = useUserContext();

	if (loggedOut === undefined) {
		return undefined;
	}
	if (!loggedOut) {
		return false;
	}

	return (
		loggedOutProfile?.sites &&
		loggedOutProfile.sites.length > 0 &&
		loggedOutProfile.sites.some(({ products }) => products && products.length > 0)
	);
};

/**
 * User is logged out and has no existing sites
 * @returns boolean | undefined
 */
export const useLoggedOutHasNoExistingSites = () => {
	const isLoggedOut = useLoggedOut();
	const { loggedOutProfile } = useUserContext();

	if (isLoggedOut === undefined) {
		return undefined;
	}
	if (!isLoggedOut) {
		return false;
	}

	const hasSites =
		loggedOutProfile?.sites &&
		loggedOutProfile.sites.length > 0 &&
		loggedOutProfile.sites.some(({ products }) => products && products.length > 0);

	return !hasSites;
};

/**
 * Returns true if the user has multiple transaction accounts
 * This cohort is used for targeting users who should see the transaction account selector
 * @returns boolean
 */
export const useMultipleTransactionAccountsCohort = () => {
	const { transactionAccounts } = useUserContext();
	return transactionAccounts.length > 1;
};

/**
 * Returns true if the user has any transaction account with non-null partner status
 * This cohort checks if any transaction account has partnerStatus with non-null values
 * @returns boolean
 */
export const useHasPartnerStatusCohort = () => {
	const { transactionAccounts } = useUserContext();

	const result = transactionAccounts.some((account) => {
		const { partnerStatus } = account;

		// Check if any field in partnerStatus is not null
		return (
			partnerStatus.discountTier !== null ||
			partnerStatus.programLevelGlobal !== null ||
			partnerStatus.externalSystemIdentifier !== null ||
			partnerStatus.programLevel !== null
		);
	});

	console.log(
		'useHasPartnerStatusCohort result:',
		result,
		'transactionAccounts:',
		transactionAccounts,
	);

	return result;
};

/**
 * Returns true if user is logged in AND has multiple transaction accounts AND has partner status
 * This is a composite cohort hook for the signup transaction selector experiment
 * @returns boolean | undefined
 */
export const useSignupTransactionSelectorCohort = () => {
	const isLoggedIn = useLoggedIn();
	const hasPartnerStatus = useHasPartnerStatusCohort();
	const hasMultipleAccounts = useMultipleTransactionAccountsCohort();

	return isLoggedIn && hasPartnerStatus && hasMultipleAccounts;
};

/**
 * User has at least one Rovo product key in their existing sites (works for both logged in and logged out users)
 * and is coming from the /software/rovo page path
 * @returns boolean | undefined
 */
export const useRovoEnabledCohort = () => {
	const loggedIn = useLoggedIn();
	const loggedOut = useLoggedOut();
	const { profile, loggedOutProfile } = useUserContext();
	const { pathname } = useLocation();

	if (loggedIn === undefined || loggedOut === undefined) {
		return undefined;
	}

	// Check if user is on the /software/rovo path (handles locale-prefixed paths like /en/software/rovo)
	// Ensure the path includes /software/rovo AND ends with it (no subpages or appended text)
	const isRovoPath =
		(pathname.includes('/software/rovo') && pathname.endsWith('/software/rovo')) ||
		pathname.includes('/search');
	const sites = loggedIn ? profile.attributes?.existingSites : loggedOutProfile?.sites;
	return (
		isRovoPath &&
		(sites?.some((site) =>
			site.products?.some(({ productKey }) => productKey === ProductKey.ROVO),
		) ??
			false)
	);
};

export const useEligibleForTwcg1856ProvisioningValueUpgrade = () => {
	if (typeof window === 'undefined') {
		return false;
	}

	const urlParams = new URLSearchParams(window.location.search);
	const products = urlParams.get('products');

	return products === 'com.atlassian.teamwork-collection';
};

export const useSubAnchor = () => {
	const { search } = useLocation();
	const params = search.split('&');

	// Paid-search URLs encode spaces as `+`, which decodeURIComponent does not
	// undo. See decode-query-param-value.ts.
	const subAnchor = sanitizeHtml(
		decodeQueryParamValue(params.filter((param) => param.includes('subAnchor'))[0]?.split('=')[1]),
	);

	return subAnchor;
};

export const useIsInContextualBanditsSubAnchor = () => {
	const subAnchor = useSubAnchor();
	return isInContextualBanditsSubAnchor(subAnchor);
};

export const useEligibleForTwcg1935CodingAgentChoice = () => {
	const isDesktop = useIsDesktop();

	if (!isDesktop) {
		return false;
	}

	if (typeof window === 'undefined') {
		return false;
	}

	const urlParams = new URLSearchParams(window.location.search);
	const teamType = urlParams.get('team_type');
	const signupSource = urlParams.get('signupSource');

	// exclude users who are journeying through /software/jira/dev.
	// these users will be forced into the coding agent question and
	// should not be included in the experiment.
	if (signupSource === JIRA_AI_NATIVE_SIGNUP_SOURCE) {
		return false;
	}

	return !!teamType && LANDING_GATE_TECH_TEAM_TYPES.includes(teamType as UserSegmentationTeamTypes);
};
